import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import UIElement from '@ckeditor/ckeditor5-engine/src/view/uielement';
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import {
  ProjectSummarySchema,
  FieldsAttr,
  ResourceSectionIdAttr,
  ResourceSectionTypeAttr,
  INSERT_PROJECT_SUMMARY_COMMAND,
} from './constants';
import { InsertProjectSummaryCommand } from './insert-project-summary-command';
import styles from './project-summary.module.css';
import { ProjectSummaryConfiguration } from './types';

export class ProjectSummary extends Plugin {
  static get requires() {
    return [Widget];
  }

  static get pluginName(): string {
    return 'ProjectSummary';
  }

  init() {
    this.defineSchema();
    this.defineConverters();
    this.editor.commands.add(
      INSERT_PROJECT_SUMMARY_COMMAND,
      new InsertProjectSummaryCommand(this.editor),
    );
  }

  private defineSchema() {
    const { schema } = this.editor.model;

    schema.register(ProjectSummarySchema, {
      isObject: true,
      allowWhere: '$block',
      allowAttributes: [
        ResourceSectionIdAttr,
        ResourceSectionTypeAttr,
        FieldsAttr,
      ],
    });
  }

  private defineConverters() {
    const { editor } = this;
    const { conversion } = editor;
    const renderProjectSummary =
      editor.config.get<ProjectSummaryConfiguration>(
        ProjectSummarySchema,
      ).renderer;

    const extraContextData = this.editor.config.get('extraContextConfig');

    conversion.for('upcast').elementToElement({
      view: {
        name: 'section',
        classes: [ProjectSummarySchema],
      },
      model: (viewElement, { writer: modelWriter }) => {
        const resourceSectionId = viewElement.getAttribute(
          `data-${ResourceSectionIdAttr}`,
        );
        const resourceSectionType = viewElement.getAttribute(
          `data-${ResourceSectionTypeAttr}`,
        );

        return modelWriter.createElement(ProjectSummarySchema, {
          [FieldsAttr]: viewElement.getAttribute(`data-${FieldsAttr}`),
          [ResourceSectionIdAttr]: resourceSectionId,
          [ResourceSectionTypeAttr]: resourceSectionType,
        });
      },
    });
    conversion.for('editingDowncast').elementToElement({
      model: ProjectSummarySchema,
      view: (modelElement, { writer: viewWriter }) => {
        const fields = modelElement.getAttribute(FieldsAttr);
        const resourceSectionId = modelElement.getAttribute(
          ResourceSectionIdAttr,
        );
        const resourceSectionType = modelElement.getAttribute(
          ResourceSectionTypeAttr,
        );

        const reactWrapper = viewWriter.createUIElement(
          'section',
          {
            class: `${ProjectSummarySchema}_react-wrapper`,
          },
          function (this: UIElement, domDocument: any) {
            const domElement = this.toDomElement(domDocument);

            renderProjectSummary(
              {
                fields: Array.isArray(fields) ? fields : [fields],
                resourceSectionId,
                resourceSectionType,
              },
              domElement,
              editor,
              extraContextData, // Pass extra context data here
            );

            return domElement;
          },
        );
        const projectSummaryView = viewWriter.createContainerElement(
          'section',
          {
            class: `${ProjectSummarySchema} ${styles.section}`,
            [`data-${FieldsAttr}`]: fields,
            [`data-${ResourceSectionIdAttr}`]: resourceSectionId,
            [`data-${ResourceSectionTypeAttr}`]: resourceSectionType,
            'data-cke-ignore-events': true,
          },
          [reactWrapper],
        );

        return toWidget(projectSummaryView, viewWriter);
      },
    });
    conversion.for('dataDowncast').elementToElement({
      model: ProjectSummarySchema,
      view: (modelItem, { writer: viewWriter }) => {
        const fields = modelItem.getAttribute(FieldsAttr);
        const resourceSectionId = modelItem.getAttribute(ResourceSectionIdAttr);
        const resourceSectionType = modelItem.getAttribute(
          ResourceSectionTypeAttr,
        );

        const projectSummaryElement = viewWriter.createRawElement('section', {
          class: `${ProjectSummarySchema} ${styles.section} ${styles.section_loading}`,
        });
        const projectSummaryView = viewWriter.createContainerElement(
          'section',
          {
            class: `${ProjectSummarySchema}`,
            [`data-${FieldsAttr}`]: fields,
            [`data-${ResourceSectionIdAttr}`]: resourceSectionId,
            [`data-${ResourceSectionTypeAttr}`]: resourceSectionType,
          },
          [projectSummaryElement],
        );

        return projectSummaryView;
      },
    });
  }
}
